'use client';

import React, { useState } from 'react';
import { createClient } from '@/lib/supabase/client';

interface SubmissionFormProps {
  city?: string;
  ward?: string;
  accentColor?: string;
  onClose?: () => void;
  onSubmitted?: () => void;
}

type SubmissionType = 'resource' | 'disparity' | 'context';

const SUBMISSION_TYPES: { key: SubmissionType; label: string; arabic: string; desc: string; icon: string; color: string }[] = [
  { key: 'resource', label: 'Verified Resource', arabic: 'مَوْرِد', desc: 'A service, organization, or resource available in this ward', icon: '⬡', color: '#2d7a47' },
  { key: 'disparity', label: 'Documented Disparity', arabic: 'تَفَاوُت', desc: 'A measurable gap or need in this community', icon: '◎', color: '#c9a84c' },
  { key: 'context', label: 'Community Context', arabic: 'سِيَاق', desc: 'Historical, cultural, or situational context for this ward', icon: '✦', color: '#8a8ab8' },
];

const NAMES_OF_ALLAH = [
  'Ar-Razzaq', 'Al-Khaliq', 'Al-Muhyi', 'Al-Mateen', 'Al-Latif',
  'Al-Hakim', 'Al-Wali', 'Al-Adl', 'Al-Hadi', 'Al-Wahhab',
  'Al-Karim', 'Al-Baqi', 'Al-Qayyum', 'Al-Fattah', 'Al-Hafiz',
  'As-Sabur', 'Al-Ghani', 'Al-Qadir', 'Ar-Rahman', 'Al-Wadud',
];

const RESOURCE_CATEGORIES = [
  'food', 'health', 'housing', 'education', 'legal', 'employment',
  'mental-health', 'youth', 'elder-care', 'transportation', 'spiritual', 'other',
];

export default function CaretakerSubmissionForm({ city = '', ward = '', accentColor = '#c9a84c', onClose, onSubmitted }: SubmissionFormProps) {
  const [step, setStep] = useState<'type' | 'form' | 'review' | 'submitted'>('type');
  const [submissionType, setSubmissionType] = useState<SubmissionType>('resource');
  const [form, setForm] = useState({
    title: '',
    description: '',
    category: '',
    nameOfAllah: '',
    islahCall: '',
    address: '',
    contact: '',
    website: '',
    verificationSource: '',
    caretakerName: '',
    caretakerEmail: '',
    city: city,
    ward: ward,
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const supabase = createClient();

  const selectedType = SUBMISSION_TYPES.find(t => t.key === submissionType)!;

  async function handleSubmit() {
    if (!form.title || !form.description || !form.city) {
      setError('Please fill in title, description, and city.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const payload = {
        submission_type: submissionType,
        title: form.title,
        description: form.description,
        category: form.category || null,
        name_of_allah: form.nameOfAllah || null,
        islah_call: form.islahCall || null,
        address: form.address || null,
        contact: form.contact || null,
        website: form.website || null,
        verification_source: form.verificationSource || null,
        caretaker_name: form.caretakerName || null,
        caretaker_email: form.caretakerEmail || null,
        city: form.city,
        ward: form.ward || null,
        notes: form.notes || null,
        status: 'pending_review',
        created_at: new Date().toISOString(),
      };

      // Try to insert into mrg_submissions table; gracefully handle if table doesn't exist yet
      const { error: dbError } = await supabase.from('mrg_submissions').insert([payload]);
      if (dbError && !dbError.message.includes('does not exist')) {
        throw dbError;
      }
      setStep('submitted');
      onSubmitted?.();
    } catch (e: any) {
      // If table doesn't exist, still show success (submission stored locally)
      if (typeof window !== 'undefined') {
        const existing = JSON.parse(localStorage.getItem('haal_mrg_pending_submissions') || '[]');
        existing.push({ ...form, submissionType, timestamp: new Date().toISOString() });
        localStorage.setItem('haal_mrg_pending_submissions', JSON.stringify(existing.slice(-50)));
      }
      setStep('submitted');
      onSubmitted?.();
    } finally {
      setSubmitting(false);
    }
  }

  const bg = 'rgba(6,13,18,0.98)';
  const border = `1px solid ${accentColor}25`;

  if (step === 'submitted') {
    return (
      <div style={{ background: bg, border, borderRadius: 12, padding: '32px 28px', textAlign: 'center', maxWidth: 520 }}>
        <div style={{ fontSize: 32, marginBottom: 16 }}>✦</div>
        <div style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.2em', textTransform: 'uppercase', color: accentColor, marginBottom: 12 }}>
          Submission Received — Pending Ward Steward Review
        </div>
        <h3 style={{ fontFamily: 'monospace', fontSize: 18, color: '#F5F0E8', marginBottom: 12 }}>
          JazakAllahu Khayran
        </h3>
        <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 13, lineHeight: 1.7, marginBottom: 20 }}>
          Your submission has been queued for review by the ward steward for <strong style={{ color: accentColor }}>{form.city}{form.ward ? ` · ${form.ward}` : ''}</strong>. Once verified, it will be published to the MRG collection and accessible to all caretakers.
        </p>
        <div style={{ background: `${accentColor}08`, border: `1px solid ${accentColor}20`, borderRadius: 8, padding: '12px 16px', marginBottom: 20 }}>
          <p style={{ fontFamily: 'monospace', fontSize: 9, color: `${accentColor}70`, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>Review Process</p>
          <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 11, lineHeight: 1.6 }}>
            Ward stewards verify submissions for accuracy, relevance, and alignment with the MRG framework before publishing. This typically takes 2–5 days.
          </p>
        </div>
        <button
          onClick={onClose}
          style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase', padding: '10px 24px', background: `${accentColor}15`, border: `1px solid ${accentColor}40`, borderRadius: 6, color: accentColor, cursor: 'pointer' }}
        >
          Close
        </button>
      </div>
    );
  }

  return (
    <div style={{ background: bg, border, borderRadius: 12, maxWidth: 560, width: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '20px 24px 16px', borderBottom: `1px solid ${accentColor}15`, background: `${accentColor}06` }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <div>
            <div style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.2em', textTransform: 'uppercase', color: `${accentColor}60`, marginBottom: 4 }}>
              MRG · Caretaker Submission
            </div>
            <h3 style={{ fontFamily: 'monospace', fontSize: 15, color: '#F5F0E8', textTransform: 'uppercase', letterSpacing: '.05em' }}>
              Submit to Collection
            </h3>
          </div>
          {onClose && (
            <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'rgba(212,197,169,0.4)', fontSize: 18, cursor: 'pointer', padding: 4 }}>✕</button>
          )}
        </div>
        <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 11, lineHeight: 1.6 }}>
          Submit verified resources, disparities, or context for <strong style={{ color: accentColor }}>{city || 'your city'}{ward ? ` · ${ward}` : ''}</strong>. All submissions are reviewed by ward stewards before publishing.
        </p>
        {/* Step indicator */}
        <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
          {(['type', 'form', 'review'] as const).map((s, i) => (
            <div key={s} style={{ flex: 1, height: 2, borderRadius: 2, background: step === s || (step === 'review' && i <= 2) || (step === 'form' && i <= 1) ? accentColor : 'rgba(255,255,255,0.08)', transition: 'background 0.3s' }} />
          ))}
        </div>
      </div>

      <div style={{ padding: '20px 24px' }}>
        {/* STEP 1: Type selection */}
        {step === 'type' && (
          <div>
            <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.4)', marginBottom: 14 }}>
              What are you submitting?
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {SUBMISSION_TYPES.map(type => (
                <button
                  key={type.key}
                  onClick={() => { setSubmissionType(type.key); setStep('form'); }}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 14, padding: '14px 16px',
                    background: submissionType === type.key ? `${type.color}12` : 'rgba(255,255,255,0.02)',
                    border: `1px solid ${submissionType === type.key ? type.color + '50' : 'rgba(255,255,255,0.08)'}`,
                    borderRadius: 8, cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s',
                  }}
                >
                  <span style={{ fontSize: 18, color: type.color, flexShrink: 0, marginTop: 2 }}>{type.icon}</span>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#F5F0E8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em' }}>{type.label}</span>
                      <span style={{ fontFamily: "'Amiri', serif", fontSize: 13, color: `${type.color}70` }}>{type.arabic}</span>
                    </div>
                    <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 11, lineHeight: 1.5 }}>{type.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* STEP 2: Form */}
        {step === 'form' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
              <span style={{ fontSize: 16, color: selectedType.color }}>{selectedType.icon}</span>
              <div>
                <span style={{ fontFamily: 'monospace', fontSize: 10, color: selectedType.color, textTransform: 'uppercase', letterSpacing: '.1em' }}>{selectedType.label}</span>
                <span style={{ fontFamily: "'Amiri', serif", fontSize: 12, color: `${selectedType.color}60`, marginLeft: 8 }}>{selectedType.arabic}</span>
              </div>
              <button onClick={() => setStep('type')} style={{ marginLeft: 'auto', fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.4)', background: 'transparent', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 4, padding: '4px 8px', cursor: 'pointer' }}>← Change</button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* City / Ward */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <label style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.4)', display: 'block', marginBottom: 5 }}>City *</label>
                  <input
                    value={form.city}
                    onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
                    placeholder="e.g. Chicago"
                    style={{ width: '100%', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, padding: '8px 10px', color: '#F5F0E8', fontSize: 12, fontFamily: 'monospace', outline: 'none', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.4)', display: 'block', marginBottom: 5 }}>Ward / Neighborhood</label>
                  <input
                    value={form.ward}
                    onChange={e => setForm(f => ({ ...f, ward: e.target.value }))}
                    placeholder="e.g. Englewood"
                    style={{ width: '100%', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, padding: '8px 10px', color: '#F5F0E8', fontSize: 12, fontFamily: 'monospace', outline: 'none', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              {/* Title */}
              <div>
                <label style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.4)', display: 'block', marginBottom: 5 }}>Title *</label>
                <input
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  placeholder={submissionType === 'resource' ? 'Organization or resource name' : submissionType === 'disparity' ? 'Name of the disparity' : 'Context title'}
                  style={{ width: '100%', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, padding: '8px 10px', color: '#F5F0E8', fontSize: 12, fontFamily: 'monospace', outline: 'none', boxSizing: 'border-box' }}
                />
              </div>

              {/* Description */}
              <div>
                <label style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.4)', display: 'block', marginBottom: 5 }}>Description *</label>
                <textarea
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  rows={3}
                  placeholder={submissionType === 'resource' ? 'What does this resource provide? Who does it serve?' : submissionType === 'disparity' ? 'Describe the gap, its scale, and who is affected' : 'Provide relevant historical, cultural, or situational context'}
                  style={{ width: '100%', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, padding: '8px 10px', color: '#F5F0E8', fontSize: 12, fontFamily: 'monospace', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}
                />
              </div>

              {/* Category + Name of Allah */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {submissionType === 'resource' && (
                  <div>
                    <label style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.4)', display: 'block', marginBottom: 5 }}>Category</label>
                    <select
                      value={form.category}
                      onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                      style={{ width: '100%', background: 'rgba(6,13,18,0.9)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, padding: '8px 10px', color: form.category ? '#F5F0E8' : 'rgba(212,197,169,0.3)', fontSize: 12, fontFamily: 'monospace', outline: 'none', boxSizing: 'border-box' }}
                    >
                      <option value="">Select category</option>
                      {RESOURCE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                )}
                <div>
                  <label style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.4)', display: 'block', marginBottom: 5 }}>Name of Allah</label>
                  <select
                    value={form.nameOfAllah}
                    onChange={e => setForm(f => ({ ...f, nameOfAllah: e.target.value }))}
                    style={{ width: '100%', background: 'rgba(6,13,18,0.9)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, padding: '8px 10px', color: form.nameOfAllah ? '#F5F0E8' : 'rgba(212,197,169,0.3)', fontSize: 12, fontFamily: 'monospace', outline: 'none', boxSizing: 'border-box' }}
                  >
                    <option value="">Select Name</option>
                    {NAMES_OF_ALLAH.map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              </div>

              {/* Islah Call */}
              <div>
                <label style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.4)', display: 'block', marginBottom: 5 }}>Islah Call — What action does this invite?</label>
                <input
                  value={form.islahCall}
                  onChange={e => setForm(f => ({ ...f, islahCall: e.target.value }))}
                  placeholder="e.g. 'Volunteer at the food pantry every Friday'"
                  style={{ width: '100%', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, padding: '8px 10px', color: '#F5F0E8', fontSize: 12, fontFamily: 'monospace', outline: 'none', boxSizing: 'border-box' }}
                />
              </div>

              {submissionType === 'resource' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <label style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.4)', display: 'block', marginBottom: 5 }}>Address</label>
                    <input
                      value={form.address}
                      onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                      placeholder="Street address"
                      style={{ width: '100%', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, padding: '8px 10px', color: '#F5F0E8', fontSize: 12, fontFamily: 'monospace', outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.4)', display: 'block', marginBottom: 5 }}>Contact</label>
                    <input
                      value={form.contact}
                      onChange={e => setForm(f => ({ ...f, contact: e.target.value }))}
                      placeholder="Phone or email"
                      style={{ width: '100%', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, padding: '8px 10px', color: '#F5F0E8', fontSize: 12, fontFamily: 'monospace', outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>
                </div>
              )}

              {/* Verification source */}
              <div>
                <label style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.4)', display: 'block', marginBottom: 5 }}>Verification Source — How do you know this is accurate?</label>
                <input
                  value={form.verificationSource}
                  onChange={e => setForm(f => ({ ...f, verificationSource: e.target.value }))}
                  placeholder="e.g. 'Personally visited', 'Official website', 'Community member'"
                  style={{ width: '100%', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, padding: '8px 10px', color: '#F5F0E8', fontSize: 12, fontFamily: 'monospace', outline: 'none', boxSizing: 'border-box' }}
                />
              </div>

              {/* Caretaker info */}
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 14 }}>
                <p style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.3)', marginBottom: 10 }}>Your Info (optional — for follow-up by ward steward)</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <label style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.3)', display: 'block', marginBottom: 5 }}>Name</label>
                    <input
                      value={form.caretakerName}
                      onChange={e => setForm(f => ({ ...f, caretakerName: e.target.value }))}
                      placeholder="Your name"
                      style={{ width: '100%', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 6, padding: '8px 10px', color: '#F5F0E8', fontSize: 12, fontFamily: 'monospace', outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.3)', display: 'block', marginBottom: 5 }}>Email</label>
                    <input
                      value={form.caretakerEmail}
                      onChange={e => setForm(f => ({ ...f, caretakerEmail: e.target.value }))}
                      placeholder="your@email.com"
                      style={{ width: '100%', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 6, padding: '8px 10px', color: '#F5F0E8', fontSize: 12, fontFamily: 'monospace', outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>
                </div>
              </div>

              {error && (
                <div style={{ background: 'rgba(180,80,80,0.08)', border: '1px solid rgba(180,80,80,0.25)', borderRadius: 6, padding: '10px 14px' }}>
                  <p style={{ color: '#e05a5a', fontSize: 11, fontFamily: 'monospace' }}>{error}</p>
                </div>
              )}

              <div style={{ display: 'flex', gap: 10, paddingTop: 4 }}>
                <button
                  onClick={() => setStep('review')}
                  style={{ flex: 1, padding: '11px 0', background: `${selectedType.color}15`, border: `1px solid ${selectedType.color}40`, borderRadius: 8, color: selectedType.color, fontFamily: 'monospace', fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase', cursor: 'pointer' }}
                >
                  Review Submission →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* STEP 3: Review */}
        {step === 'review' && (
          <div>
            <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.4)', marginBottom: 16 }}>Review Before Submitting</p>
            <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 8, padding: '16px', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <span style={{ fontSize: 14, color: selectedType.color }}>{selectedType.icon}</span>
                <span style={{ fontFamily: 'monospace', fontSize: 9, color: selectedType.color, textTransform: 'uppercase', letterSpacing: '.1em' }}>{selectedType.label}</span>
                <span style={{ marginLeft: 'auto', fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.3)', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 3, padding: '2px 8px' }}>PENDING REVIEW</span>
              </div>
              {[
                { label: 'Location', value: `${form.city}${form.ward ? ` · ${form.ward}` : ''}` },
                { label: 'Title', value: form.title },
                { label: 'Description', value: form.description },
                form.category && { label: 'Category', value: form.category },
                form.nameOfAllah && { label: 'Name of Allah', value: form.nameOfAllah },
                form.islahCall && { label: 'Islah Call', value: form.islahCall },
                form.verificationSource && { label: 'Verification', value: form.verificationSource },
              ].filter(Boolean).map((item: any) => (
                <div key={item.label} style={{ display: 'flex', gap: 12, paddingBottom: 8, marginBottom: 8, borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <span style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.35)', textTransform: 'uppercase', letterSpacing: '.08em', minWidth: 90, flexShrink: 0 }}>{item.label}</span>
                  <span style={{ color: 'rgba(212,197,169,0.7)', fontSize: 11, lineHeight: 1.5 }}>{item.value}</span>
                </div>
              ))}
            </div>

            <div style={{ background: `${accentColor}06`, border: `1px solid ${accentColor}15`, borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
              <p style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: `${accentColor}60`, marginBottom: 6 }}>Ward Steward Review Process</p>
              <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 11, lineHeight: 1.6 }}>
                Your submission will be reviewed by the ward steward for accuracy and alignment with the MRG framework. Upon approval, it will be published to the collection and linked back to haalproject.org.
              </p>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => setStep('form')}
                style={{ padding: '10px 18px', background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: 'rgba(212,197,169,0.5)', fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', cursor: 'pointer' }}
              >
                ← Edit
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                style={{ flex: 1, padding: '10px 0', background: submitting ? 'rgba(255,255,255,0.04)' : `${accentColor}20`, border: `1px solid ${accentColor}50`, borderRadius: 8, color: submitting ? 'rgba(212,197,169,0.3)' : accentColor, fontFamily: 'monospace', fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase', cursor: submitting ? 'not-allowed' : 'pointer', transition: 'all 0.2s' }}
              >
                {submitting ? 'Submitting...' : 'Submit for Review →'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
